import type { TimetableDay } from './types';
import { normaliseTime, pad2 } from './time';

/**
 * Reads a masjid timetable spreadsheet (CSV), the only source of prayer times. Masjids export these from many tools,
 * so headers are matched loosely: "Fajr", "Fajr Begins", "Fajr Jamaat", "Fajr Iqamah", "Zuhr", "Shuruq", "Esha",
 * "Jumuah 1", "Sehri Ends", "Tarawih"… Dates may be 2026-03-01, 01/03/2026 (day first unless the file is clearly
 * month first) or "1 Mar 2026". A Hijri column is allowed and ignored: NoorView works the Hijri date out itself.
 */

type Key = keyof TimetableDay;
/** Columns with one value each (no separate jama'ah column). Checked before the prayers. */
const SINGLE_ALIASES: Array<[Key, RegExp]> = [
  ['sehri', /^(sehri|sehr|suhoor|suhur|sahur|sahoor|imsak)/],
  ['tarawih', /^(tara[a]?w[e]?eh|tarawih|taraweeh|taraveeh)/],
  ['jumuah2', /^(jum|friday).*(2|second|2nd)/],
  ['jumuah1', /^(jum|friday)/],
];
const PRAYER_ALIASES: Array<[Key, Key, RegExp]> = [
  ['fajr', 'fajrJamaah', /^(fajr|fajar|subh|sub[h]?)/],
  ['sunrise', 'sunrise', /^(sunrise|shuruq|shurooq|sun rise|ishraq)/],
  ['dhuhr', 'dhuhrJamaah', /^(dhuhr|zuhr|zohr|zuhur|duhr|dhur|thuhr)/],
  ['asr', 'asrJamaah', /^(asr|asar)/],
  ['maghrib', 'maghribJamaah', /^(maghrib|magrib|iftar)/],
  ['isha', 'ishaJamaah', /^(isha|esha|ishaa)/],
];
const JAMAAH_RE = /(jama|jamaa?t|jamat|iqam|iqama|congreg|prayer time)/;

export interface CsvResult { days: TimetableDay[]; errors: string[]; warnings: string[]; columns: Record<string, string> }

/** RFC 4180 CSV parsing (quoted fields, commas and new lines inside quotes). Also accepts semicolons and tabs. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const first = src.split(/\r?\n/, 1)[0] ?? '';
  const delim = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : first.includes('\t') && !first.includes(',') ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [], field = '', q = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (q) {
      if (c === '"') { if (src[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += c;
    } else if (c === '"') q = true;
    else if (c === delim) { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };

export function parseDate(input: string, fallbackYear: number, order: 'dmy' | 'mdy' = 'dmy'): string | null {
  const s = input.trim().toLowerCase().replace(/(st|nd|rd|th)\b/g, '').replace(/,/g, ' ');
  let y: number | undefined, m: number | undefined, d: number | undefined;
  let r = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/.exec(s);
  if (r) { y = +r[1]!; m = +r[2]!; d = +r[3]!; }
  else if ((r = /^(\d{1,2})[-/.](\d{1,2})(?:[-/.](\d{2,4}))?/.exec(s))) {
    const a = +r[1]!, b = +r[2]!;
    [d, m] = order === 'dmy' ? [a, b] : [b, a];
    y = r[3] ? (r[3].length === 2 ? 2000 + +r[3] : +r[3]) : fallbackYear;
  } else if ((r = /^(?:[a-z]+\s+)?(\d{1,2})[\s-]+([a-z]{3,9})[\s-]*(\d{4})?/.exec(s))) {
    d = +r[1]!; m = MONTHS[r[2]!.slice(0, 3)]; y = r[3] ? +r[3] : fallbackYear;
  }
  if (!y || !m || !d || m > 12 || d > 31) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  if (t.getUTCMonth() !== m - 1) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export function importTimetableCsv(text: string, opts: { year?: number; dateOrder?: 'dmy' | 'mdy' } = {}): CsvResult {
  const rows = parseCsv(text);
  const errors: string[] = [], warnings: string[] = [];
  if (rows.length < 2) return { days: [], errors: ['The file has no rows of times.'], warnings, columns: {} };

  // Find the header row: the first row that mentions a date column and at least three prayers.
  let headerAt = rows.findIndex((r) => {
    const cells = r.map((c) => c.trim().toLowerCase());
    return cells.some((c) => /date|day/.test(c)) && PRAYER_ALIASES.filter(([, , re]) => cells.some((c) => re.test(c))).length >= 3;
  });
  if (headerAt < 0) headerAt = 0;
  const header = rows[headerAt]!.map((c) => c.trim().toLowerCase().replace(/[’'`]/g, ''));
  const map: Array<Key | null> = header.map(() => null);
  const columns: Record<string, string> = {};
  const dateCol = header.findIndex((c) => /^gregorian/.test(c)) >= 0 ? header.findIndex((c) => /^gregorian/.test(c)) : header.findIndex((c) => !/hijri|islamic/.test(c) && /^date|date$|^day$/.test(c));
  if (dateCol < 0) return { days: [], errors: ['Couldn’t find a Date column.'], warnings, columns };
  header.forEach((c, i) => {
    if (i === dateCol || /hijri|islamic/.test(c)) return;
    const single = SINGLE_ALIASES.find(([, re]) => re.test(c));
    if (single) { if (!map.includes(single[0])) { map[i] = single[0]; columns[single[0]] = rows[headerAt]![i]!.trim(); } return; }
    for (const [start, jamaah, re] of PRAYER_ALIASES) {
      if (!re.test(c)) continue;
      const key = JAMAAH_RE.test(c) && start !== 'sunrise' ? jamaah : start;
      // If a prayer has two unlabelled columns, the second is the jama'ah time.
      const k: Key = map.includes(key) && key === start && start !== 'sunrise' ? jamaah : key;
      if (!map.includes(k)) { map[i] = k; columns[k] = rows[headerAt]![i]!.trim(); }
      break;
    }
  });
  if (!map.some((k) => k === 'fajr' || k === 'dhuhr')) errors.push('Couldn’t find prayer time columns (Fajr, Dhuhr, Asr, Maghrib, Isha).');

  const year = opts.year ?? new Date().getFullYear();
  const order = opts.dateOrder ?? guessDateOrder(rows.slice(headerAt + 1).map((r) => r[dateCol] ?? ''));
  const days = new Map<string, TimetableDay>();
  for (let r = headerAt + 1; r < rows.length; r++) {
    const row = rows[r]!;
    const raw = row[dateCol]?.trim() ?? '';
    if (!raw) continue;
    const date = parseDate(raw, year, order);
    if (!date) { if (errors.length < 20) errors.push(`Row ${r + 1}: couldn’t read the date “${raw}”.`); continue; }
    const day: TimetableDay = { date };
    map.forEach((k, i) => {
      if (!k) return;
      const v = row[i]?.trim();
      // Blank, the template's formula left as text, or a spreadsheet error (#VALUE!): worked out below where it can be.
      if (!v || v.startsWith('=') || v.startsWith('#')) return;
      // A spreadsheet can save a time as a fraction of a day (0.5 is 12:00).
      const frac = /^0?\.\d+$|^0$/.test(v) ? Math.round(parseFloat(v) * 1440) % 1440 : null;
      const t = frac != null ? `${pad2(Math.floor(frac / 60))}:${pad2(frac % 60)}` : normaliseTime(v);
      if (t) (day as unknown as Record<string, string>)[k] = t;
      else if (!/^[-–—]+$/.test(v) && warnings.length < 20) warnings.push(`Row ${r + 1}: “${v}” isn’t a time, so it was skipped.`);
    });
    // 12-hour timetables often write afternoon prayers without pm: lift them into the afternoon.
    for (const k of ['dhuhr', 'asr', 'maghrib', 'isha', 'dhuhrJamaah', 'asrJamaah', 'maghribJamaah', 'ishaJamaah', 'jumuah1', 'jumuah2', 'tarawih'] as const) {
      const v = day[k];
      const noonish = k.startsWith('dhuhr') || k.startsWith('jumuah');
      if (v && +v.slice(0, 2) < (noonish ? 10 : 12)) day[k] = `${pad2(+v.slice(0, 2) + 12)}${v.slice(2)}`;
    }
    fillDerivedTimes(day);
    // Rows of the template that haven't been filled in yet are left out, so they never wipe a day's times.
    if (Object.keys(day).length === 1) continue;
    if (days.has(date)) warnings.push(`${date} appears twice; the last row was used.`);
    days.set(date, day);
  }
  if (!days.size && !errors.length) errors.push('No rows with a date and times were found.');
  return { days: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)), errors, warnings, columns };
}

/** Day first unless a date in the file can only be month first (e.g. 03/25/2026). */
function guessDateOrder(values: string[]): 'dmy' | 'mdy' {
  let dmy = 0, mdy = 0;
  for (const v of values) {
    const r = /^\s*(\d{1,2})[-/.](\d{1,2})/.exec(v);
    if (!r) continue;
    if (+r[1]! > 12) dmy++;
    else if (+r[2]! > 12) mdy++;
  }
  return mdy > dmy ? 'mdy' : 'dmy';
}

/** The template's columns, in the order masjids usually write them. The Hijri column may be left blank. */
export const TIMETABLE_COLUMNS: Array<[Key | 'hijri', string]> = [
  ['date', 'Gregorian Date'], ['hijri', 'Hijri Date'], ['sunrise', 'Sunrise'],
  ['fajr', 'Fajr Begins'], ['fajrJamaah', 'Fajr Jamaat'], ['dhuhr', 'Zuhr Begins'], ['dhuhrJamaah', 'Zuhr Jamaat'],
  ['asr', 'Asr Begins'], ['asrJamaah', 'Asr Jamaat'], ['maghrib', 'Maghrib Begins'], ['maghribJamaah', 'Maghrib Jamaat'],
  ['isha', 'Isha Begins'], ['ishaJamaah', 'Isha Jamaat'], ['jumuah1', 'Jumuah 1'], ['jumuah2', 'Jumuah 2'],
  ['sehri', 'Sehri Ends'], ['tarawih', 'Tarawih'],
];

const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
/** DD/MM/YYYY, as UK masjids write it. */
const dmyOf = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

export function exportTimetableCsv(days: readonly TimetableDay[]): string {
  const rows = days.map((d) => TIMETABLE_COLUMNS.map(([k]) => (k === 'date' ? dmyOf(d.date) : k === 'hijri' ? '' : esc(String(d[k] ?? '')))).join(','));
  return [TIMETABLE_COLUMNS.map(([, l]) => l).join(','), ...rows].join('\r\n');
}

/** How many minutes before Fajr begins Sehri ends, when the timetable doesn't say. */
export const SEHRI_BEFORE_FAJR = 10;

/**
 * Times worked out from others when they're left blank: Sehri ends 10 minutes before Fajr begins, and Maghrib's
 * jama'ah is at sunset, when Maghrib begins. A time the masjid gives is always kept.
 */
export function fillDerivedTimes(day: TimetableDay): TimetableDay {
  if (!day.sehri && day.fajr) {
    const m = (+day.fajr.slice(0, 2) * 60 + +day.fajr.slice(3, 5) - SEHRI_BEFORE_FAJR + 1440) % 1440;
    day.sehri = `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
  }
  if (!day.maghribJamaah && day.maghrib) day.maghribJamaah = day.maghrib;
  return day;
}

/** The spreadsheet column letter of a template column (A, B, … Q). */
const colOf = (k: Key | 'hijri') => String.fromCharCode(65 + TIMETABLE_COLUMNS.findIndex(([c]) => c === k));

/**
 * A blank timetable to fill in: one row per date from `from` for `count` days, times left empty. Sehri Ends and
 * Maghrib Jamaat hold formulas, so a spreadsheet fills them in as Fajr and Maghrib are typed (10 minutes before Fajr;
 * the same as Maghrib begins). They can be typed over. If an app leaves a formula as text, the upload works it out.
 */
export function timetableTemplateCsv(from: string, count: number): string {
  const out: string[] = [TIMETABLE_COLUMNS.map(([, l]) => l).join(',')];
  const [y, m, d] = from.split('-').map(Number);
  const F = colOf('fajr'), MG = colOf('maghrib');
  for (let i = 0; i < count; i++) {
    const t = new Date(Date.UTC(y!, m! - 1, d! + i));
    const iso = `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
    const r = i + 2;
    out.push(TIMETABLE_COLUMNS.map(([k]) => (k === 'date' ? dmyOf(iso)
      : k === 'sehri' ? esc(`=IF(${F}${r}="","",TEXT(MOD(${F}${r}-TIME(0,${SEHRI_BEFORE_FAJR},0),1),"hh:mm"))`)
      : k === 'maghribJamaah' ? esc(`=IF(${MG}${r}="","",TEXT(${MG}${r},"hh:mm"))`)
      : '')).join(','));
  }
  return out.join('\r\n');
}
